import type {
  ChatAssistantSegment,
  ChatMessageFeedback,
  ChatMessageViewModel,
  ChatToolActivity,
  RuntimeMessageDto,
} from '@/features/shared/chat/types'
import {
  extractRuntimeAttachmentsFromMarkdown,
  mergeRuntimeAttachments,
  normalizeRuntimeAttachments,
} from '@/features/shared/chat/attachments/attachments'
import {
  normalizeCitationResources,
  normalizeCitationResourcesFromMetadata,
} from '@/features/shared/chat/citations'
import { normalizeRuntimeSsePayload } from '@/features/shared/chat/stream/sse'
import {
  insertHistoricalToolActivitySegments,
  isRuntimeToolSseEvent,
  reduceToolActivities,
} from '@/features/shared/chat/stream/tool-events'

type Translate = (key: string) => string

interface PersistedReasoningSegment {
  beforeToolTurnId: string | null
  content: string
}

function readText(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

function readMetadataText(metadata: RuntimeMessageDto['metadata'], key: string) {
  if (!metadata || typeof metadata !== 'object') {
    return undefined
  }

  return readText(metadata[key])
}

function normalizePersistedReasoningSegments(
  message: RuntimeMessageDto,
): PersistedReasoningSegment[] {
  const metadataSegments = message.metadata?.reasoning_segments
  const rawSegments = Array.isArray(message.reasoning_segments)
    ? message.reasoning_segments
    : Array.isArray(metadataSegments)
      ? metadataSegments
      : []

  return rawSegments.reduce<PersistedReasoningSegment[]>((segments, rawSegment) => {
    if (!rawSegment || typeof rawSegment !== 'object') {
      return segments
    }

    const segment = rawSegment as Record<string, unknown>
    const content = readText(segment.content)
    const rawTurnId = segment.before_tool_turn_id

    if (!content) {
      return segments
    }

    if (rawTurnId === null) {
      segments.push({ beforeToolTurnId: null, content })
      return segments
    }

    const beforeToolTurnId = readText(rawTurnId)
    if (!beforeToolTurnId) {
      return segments
    }

    segments.push({ beforeToolTurnId, content })
    return segments
  }, [])
}

function normalizeFeedback(
  feedback: RuntimeMessageDto['feedback'],
): ChatMessageFeedback | undefined {
  const rating = feedback?.rating

  if (rating === 'like' || rating === 'dislike' || rating === null) {
    return { rating }
  }

  return undefined
}

function normalizeHistoryToolEvents(events: RuntimeMessageDto['tool_events'], runId?: string) {
  if (!runId || !Array.isArray(events)) {
    return []
  }

  return events.reduce<ChatToolActivity[]>((tools, payload) => {
    const event = normalizeRuntimeSsePayload(payload)

    if (
      !isRuntimeToolSseEvent(event) ||
      event.version !== '2.0' ||
      !Number.isInteger(event.sequence) ||
      (event.sequence ?? 0) <= 0 ||
      event.run_id !== runId
    ) {
      return tools
    }

    return reduceToolActivities(tools, event)
  }, [])
}

function pushHistoryAnswerSegment(
  segments: ChatAssistantSegment[],
  baseId: string,
  content: string,
  index: number,
) {
  const trimmedContent = content.trim()

  if (!trimmedContent) {
    return index
  }

  const previousSegment = segments.at(-1)

  if (previousSegment?.type === 'answer') {
    previousSegment.content = `${previousSegment.content}\n\n${trimmedContent}`
    return index
  }

  segments.push({
    content: trimmedContent,
    id: `${baseId}-answer-${String(index)}`,
    type: 'answer',
  })

  return index + 1
}

function pushHistoryThoughtSegment(
  segments: ChatAssistantSegment[],
  baseId: string,
  content: string,
  index: number,
) {
  const trimmedContent = content.trim()

  if (!trimmedContent) {
    return index
  }

  segments.push({
    content: trimmedContent,
    id: `${baseId}-thought-${String(index)}`,
    type: 'thought',
  })

  return index + 1
}

function normalizeComparableMessage(content: string) {
  return content.replace(/\s+/g, ' ').trim()
}

function orderAssistantSegments(segments: ChatAssistantSegment[]) {
  return [
    ...segments.filter((segment) => segment.type === 'thought'),
    ...segments.filter((segment) => segment.type === 'answer'),
  ]
}

/**
 * 将历史消息中的 `<think>...</think>` 兼容格式拆成可独立渲染的思考段和回答段。
 * provider 没有结构化 thought 字段时，前端仍能复原消息层次。
 */
export function splitMarkdownIntoSegments(markdown: string, baseId = 'markdown') {
  const segments: ChatAssistantSegment[] = []
  const thoughts: string[] = []
  const answers: string[] = []
  const openTagPattern = /<think>/gi
  let cursor = 0
  let segmentIndex = 0

  for (let openTagMatch = openTagPattern.exec(markdown); openTagMatch; ) {
    const answerContent = markdown.slice(cursor, openTagMatch.index)
    segmentIndex = pushHistoryAnswerSegment(segments, baseId, answerContent, segmentIndex)

    const thoughtStartIndex = openTagPattern.lastIndex
    const closeTagPattern = /<\/think>/gi
    closeTagPattern.lastIndex = thoughtStartIndex
    const closeTagMatch = closeTagPattern.exec(markdown)
    const thoughtContent = closeTagMatch
      ? markdown.slice(thoughtStartIndex, closeTagMatch.index)
      : markdown.slice(thoughtStartIndex)
    const trimmedThought = thoughtContent.trim()

    if (trimmedThought) {
      thoughts.push(trimmedThought)
    }

    segmentIndex = pushHistoryThoughtSegment(segments, baseId, thoughtContent, segmentIndex)
    cursor = closeTagMatch ? closeTagMatch.index + closeTagMatch[0].length : markdown.length
    openTagPattern.lastIndex = cursor

    if (!closeTagMatch) {
      break
    }

    openTagMatch = openTagPattern.exec(markdown)
  }

  segmentIndex = pushHistoryAnswerSegment(segments, baseId, markdown.slice(cursor), segmentIndex)

  for (const segment of segments) {
    if (segment.type === 'answer') {
      answers.push(segment.content)
    }
  }

  const content = answers.join('\n\n').trim()
  const thought = thoughts.join('\n\n').trim()
  const duplicatedThought =
    Boolean(content) && normalizeComparableMessage(content) === normalizeComparableMessage(thought)
  const visibleSegments = duplicatedThought
    ? segments.filter((segment) => segment.type === 'answer')
    : segments

  return {
    content,
    segments: orderAssistantSegments(visibleSegments),
    thought: duplicatedThought ? undefined : thought || undefined,
  }
}

export function splitThoughtFromMarkdown(markdown: string) {
  const { content, thought } = splitMarkdownIntoSegments(markdown)

  return {
    content,
    thought,
  }
}

/**
 * 将 runtime 历史 DTO 归一为聊天 UI 的消息模型。
 * 这里兼容 query/answer、content/text、thought/reasoning_content 等历史字段差异。
 */
export function normalizeHistoryMessages(messages: RuntimeMessageDto[], t: Translate) {
  const normalized: ChatMessageViewModel[] = []

  for (const message of messages) {
    const createdAt =
      typeof message.created_at === 'string' || typeof message.created_at === 'number'
        ? String(message.created_at)
        : undefined
    const baseId = message.id ?? message.message_id ?? `history-${String(normalized.length)}`
    const messageRunId = readText(message.message_id ?? message.id)
    const query = readText(message.query)
    const rawAnswer = readText(message.answer ?? message.content ?? message.text)
    const role = message.role?.trim().toLowerCase()
    const messageFiles = normalizeRuntimeAttachments(message.message_files)
    const userAttachments = messageFiles.filter(
      (file) => file.belongs_to === 'user' || (role === 'user' && !file.belongs_to),
    )
    const answerAttachments =
      role === 'user'
        ? { attachments: [], content: rawAnswer ?? '' }
        : extractRuntimeAttachmentsFromMarkdown(rawAnswer ?? '', 'assistant')
    const assistantAttachments =
      role === 'user'
        ? []
        : mergeRuntimeAttachments(
            messageFiles.filter((file) => file.belongs_to !== 'user'),
            answerAttachments.attachments,
          )
    const citations =
      role === 'user'
        ? []
        : [
            ...normalizeCitationResources(message.retriever_resources),
            ...normalizeCitationResourcesFromMetadata(message.metadata),
          ]
    const explicitThought =
      readText(message.thought) ??
      readText(message.reasoning_content) ??
      readMetadataText(message.metadata, 'thought') ??
      readMetadataText(message.metadata, 'reasoning_content')
    const persistedReasoningSegments =
      role === 'user' ? [] : normalizePersistedReasoningSegments(message)
    const tools =
      role === 'user' ? [] : normalizeHistoryToolEvents(message.tool_events, messageRunId)

    if (query) {
      normalized.push({
        content: query,
        createdAt,
        id: `${baseId}-query`,
        role: 'user',
        state: 'done',
        ...(userAttachments.length > 0 ? { attachments: userAttachments } : {}),
      })
    }

    // ADP 这类 provider 会把单条用户历史同时归一出 query 和 content/text。
    // 对 role=user 的同文内容只渲染一次，否则历史视图会出现两个完全相同的用户气泡。
    const duplicatedUserContent = role === 'user' && Boolean(query) && rawAnswer === query

    if (
      (rawAnswer && !duplicatedUserContent) ||
      explicitThought ||
      persistedReasoningSegments.length > 0 ||
      assistantAttachments.length > 0 ||
      tools.length > 0
    ) {
      const splitAnswer = splitMarkdownIntoSegments(answerAttachments.content, baseId)
      const persistedThought = persistedReasoningSegments
        .map((segment) => segment.content)
        .join('\n\n')
      const duplicatedPersistedThought =
        Boolean(persistedThought && splitAnswer.content) &&
        normalizeComparableMessage(persistedThought) ===
          normalizeComparableMessage(splitAnswer.content)
      const visiblePersistedReasoningSegments = duplicatedPersistedThought
        ? []
        : persistedReasoningSegments
      const duplicatedExplicitThought =
        Boolean(explicitThought && splitAnswer.content) &&
        normalizeComparableMessage(explicitThought ?? '') ===
          normalizeComparableMessage(splitAnswer.content)
      const visibleExplicitThought =
        visiblePersistedReasoningSegments.length > 0 || duplicatedExplicitThought
          ? undefined
          : explicitThought
      const thought =
        visiblePersistedReasoningSegments.length > 0
          ? persistedThought
          : (visibleExplicitThought ?? splitAnswer.thought)
      const segments: ChatAssistantSegment[] =
        visiblePersistedReasoningSegments.length > 0 || visibleExplicitThought
          ? splitAnswer.segments.filter((segment) => segment.type === 'answer')
          : [...splitAnswer.segments]

      if (role !== 'user' && visiblePersistedReasoningSegments.length > 0) {
        segments.unshift(
          ...visiblePersistedReasoningSegments.map((segment, index) => ({
            beforeToolTurnId: segment.beforeToolTurnId,
            content: segment.content,
            id: `${baseId}-thought-persisted-${String(index)}`,
            type: 'thought' as const,
          })),
        )
      } else if (role !== 'user' && visibleExplicitThought) {
        // 显式 thought 比 `<think>` 解析结果优先展示，避免 reasoning 内容被混进回答正文。
        segments.unshift({
          content: visibleExplicitThought,
          id: `${baseId}-thought-explicit`,
          type: 'thought',
        })
      }

      const timelineSegments =
        role === 'user' ? segments : insertHistoricalToolActivitySegments(segments, tools, baseId)

      const normalizedMessage: ChatMessageViewModel = {
        content: role === 'user' ? (rawAnswer ?? '') : splitAnswer.content,
        createdAt,
        id: `${baseId}-answer`,
        messageId: message.message_id ?? message.id ?? undefined,
        role: role === 'user' ? 'user' : 'assistant',
        segments: role === 'user' || timelineSegments.length === 0 ? undefined : timelineSegments,
        state: 'done',
        thought: role === 'user' ? undefined : thought,
        ...(tools.length > 0 ? { tools } : {}),
        ...(role === 'user'
          ? userAttachments.length > 0
            ? { attachments: userAttachments }
            : {}
          : assistantAttachments.length > 0
            ? { attachments: assistantAttachments }
            : {}),
        ...(citations.length > 0 ? { citations } : {}),
      }
      const feedback = role === 'user' ? undefined : normalizeFeedback(message.feedback)

      if (feedback) {
        normalizedMessage.feedback = feedback
      }

      normalized.push(normalizedMessage)
    }
  }

  if (normalized.length === 0) {
    return [
      {
        content: t('messages.emptyHistory'),
        id: 'history-empty',
        role: 'system' as const,
        state: 'done' as const,
      },
    ]
  }

  return normalized
}
